import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * View store for the AI → LLM Providers admin view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Like the DevOps Connections area, the LLM provider services
 * are read through the REST data client ({@link createRestApi}) rather than a
 * generated ServiceStore, so this store holds the fetched provider list locally.
 *
 * SECRET HANDLING: the API key is write-only. The API never returns it — a
 * provider record carries only a `hasApiKey` boolean, never the secret. The
 * form's `apiKey` field is never seeded from a record and is cleared after every
 * save, so it can never be repopulated from server data. On edit a blank key
 * means "keep the existing key"; a non-empty value rotates it.
 */

/** The five supported provider vendor types (mirrors the API's LlmProviderType). */
export type LlmProviderType =
  | "anthropic"
  | "openai"
  | "gemini"
  | "azureOpenAI"
  | "ollama";

export const LLM_PROVIDER_TYPES: LlmProviderType[] = [
  "anthropic",
  "openai",
  "gemini",
  "azureOpenAI",
  "ollama",
];

/** UI metadata per provider type: label + which credentials/fields it needs. */
export const LLM_PROVIDER_TYPE_META: Record<
  LlmProviderType,
  { label: string; needsKey: boolean; needsEndpoint: boolean }
> = {
  anthropic: { label: "Anthropic (Claude)", needsKey: true, needsEndpoint: false },
  openai: { label: "OpenAI", needsKey: true, needsEndpoint: false },
  gemini: { label: "Google Gemini", needsKey: true, needsEndpoint: false },
  azureOpenAI: { label: "Azure OpenAI", needsKey: true, needsEndpoint: true },
  ollama: { label: "Ollama (local)", needsKey: false, needsEndpoint: true },
};

export const llmProviderTypeLabel = (type?: string): string =>
  (type && LLM_PROVIDER_TYPE_META[type as LlmProviderType]?.label) || type || "";

/** True when the provider type requires an API key (everything except Ollama). */
export const llmProviderNeedsKey = (type?: string): boolean =>
  !!type && !!LLM_PROVIDER_TYPE_META[type as LlmProviderType]?.needsKey;

/** True when the provider type requires an endpoint (azureOpenAI + ollama). */
export const llmProviderNeedsEndpoint = (type?: string): boolean =>
  !!type && !!LLM_PROVIDER_TYPE_META[type as LlmProviderType]?.needsEndpoint;

export type LlmProviderTypeFilter = "all" | LlmProviderType;

/**
 * A configured LLM provider as the API serialises it. The encrypted API key is
 * NEVER returned — the API masks it to a `hasApiKey` boolean (and an
 * `apiKeyMasked` placeholder) so the UI can show whether a key is on file
 * without exposing it.
 */
export interface LlmProvider {
  id: string;
  type: LlmProviderType | string;
  name: string;
  /** Write-only in the UI — the API never returns the key itself. */
  hasApiKey?: boolean;
  apiKeyMasked?: string;
  endpoint?: string | null;
  defaultModel?: string | null;
  apiVersion?: string | null;
  enabled?: boolean;
  status?: string | null;
  lastValidatedAt?: string | null;
}

/** Editable subset of a provider used by the create/edit form. */
export interface LlmProviderFormData {
  type: LlmProviderType;
  name: string;
  /** Write-only — submitted to the API, never rendered back. */
  apiKey: string;
  endpoint: string;
  defaultModel: string;
  apiVersion: string;
  enabled: boolean;
}

/** Raw response of the provider `validate` custom action. */
interface LlmProviderValidateResponse {
  status?: string;
  message?: string;
  lastValidatedAt?: string | null;
  validation?: {
    ok?: boolean;
    status?: string;
    message?: string;
  };
}

/** Normalised (flat) result of the provider `validate` action for the UI alert. */
export interface LlmProviderTestResult {
  status: string;
  message?: string;
}

/** Payload sent to trigger the provider `validate` action (POST, no secret). */
interface LlmProviderValidatePayload {
  action: "validate";
  id: string;
}

class LlmProvidersStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  /**
   * True when the last `load()` failed. `error` is shared with write failures
   * (a rejected remove), which leave the list intact; this flag is what tells
   * the body the LIST itself is unknown — tiles read "—" and the table shows
   * the failure, never "No LLM providers yet".
   */
  loadFailed = false;

  // Header filter
  searchQuery = "";
  typeFilter: LlmProviderTypeFilter = "all";

  // Create/edit form state (shared by the provider form dialog)
  editingId: string | null = null;
  formData: LlmProviderFormData = LlmProvidersStore.emptyForm();
  saving = false;
  formError: string | null = null;

  // Per-provider "Test" (validate) state
  testingId: string | null = null;
  lastTestResult: LlmProviderTestResult | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `llm-providers` ServiceStore (see serviceStores/ai.stores.ts). */
  private get service(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("llm-providers") ?? this.rootStore.llmProvidersStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Provider list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get providers(): LlmProvider[] {
    return (this.service?.entities ?? []) as unknown as LlmProvider[];
  }

  /** A blank create form: default to Anthropic, enabled, no secret seeded. */
  private static emptyForm(): LlmProviderFormData {
    return {
      type: "anthropic",
      name: "",
      apiKey: "",
      endpoint: "",
      defaultModel: "",
      apiVersion: "",
      enabled: true,
    };
  }

  // ---- Derived data -------------------------------------------------------

  providerName(id?: string | null): string {
    if (!id) return "";
    return this.providers.find((p) => p.id === id)?.name || "";
  }

  providerById(id?: string | null): LlmProvider | null {
    if (!id) return null;
    return this.providers.find((p) => p.id === id) || null;
  }

  get filteredProviders(): LlmProvider[] {
    let result = this.providers;

    if (this.typeFilter !== "all") {
      result = result.filter((p) => p.type === this.typeFilter);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          llmProviderTypeLabel(p.type).toLowerCase().includes(q) ||
          p.defaultModel?.toLowerCase().includes(q) ||
          p.endpoint?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /** True when a search or type filter is narrowing the list. */
  get hasActiveFilters(): boolean {
    return this.typeFilter !== "all" || this.searchQuery.trim() !== "";
  }

  get totalCount(): number {
    return this.providers.length;
  }

  get enabledCount(): number {
    return this.providers.filter((p) => p.enabled).length;
  }

  get verifiedCount(): number {
    return this.providers.filter((p) => p.status === "verified" || p.status === "valid")
      .length;
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setTypeFilter(filter: LlmProviderTypeFilter) {
    this.typeFilter = filter;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.loading = true;
    this.error = null;
    this.loadFailed = false;
    try {
      // ServiceStore.find populates its entity cache, which `providers` reads.
      // findOrThrow: a bare find SWALLOWS a failure (resolves null), which
      // left this catch dead and the page reading "No LLM providers yet".
      await findOrThrow(this.service, {
        query: { $limit: 200, $sort: { name: 1 } },
      });
    } catch (err: unknown) {
      // The sign-out ended it: the store was reset, and `loadFailed` with no
      // error would hide the list and show an error banner with nothing in it.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load providers");
        this.loadFailed = true;
      });

      console.error("Failed to load LLM providers:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.load();
  }

  // ---- Create / edit form -------------------------------------------------

  startCreate() {
    this.editingId = null;
    this.formError = null;
    this.lastTestResult = null;
    // apiKey starts empty and stays write-only; default to Anthropic, enabled.
    this.formData = LlmProvidersStore.emptyForm();
  }

  startEdit(provider: LlmProvider) {
    this.editingId = provider.id;
    this.formError = null;
    this.lastTestResult = null;
    // IMPORTANT: never seed `apiKey` from a record — the API does not return it.
    // Leaving it blank means "keep the existing key"; a non-empty value on save
    // rotates it.
    this.formData = {
      type: (LLM_PROVIDER_TYPES.includes(provider.type as LlmProviderType)
        ? (provider.type as LlmProviderType)
        : "anthropic"),
      name: provider.name || "",
      apiKey: "",
      endpoint: provider.endpoint || "",
      defaultModel: provider.defaultModel || "",
      apiVersion: provider.apiVersion || "",
      enabled: provider.enabled ?? true,
    };
  }

  get isEditing(): boolean {
    return !!this.editingId;
  }

  setFormField<K extends keyof LlmProviderFormData>(
    field: K,
    value: LlmProviderFormData[K]
  ) {
    // Changing the provider type makes the previous type's endpoint/apiVersion
    // irrelevant — clear them so a stale value can't be persisted for the wrong
    // type (e.g. an Azure endpoint left on an Anthropic provider).
    if (field === "type") {
      this.formData = { ...this.formData, type: value as LlmProviderFormData["type"], endpoint: "", apiVersion: "" };
      return;
    }
    this.formData = { ...this.formData, [field]: value };
  }

  get isFormValid(): boolean {
    const hasBasics = !!this.formData.type && !!this.formData.name.trim();
    if (!hasBasics) return false;
    // Azure OpenAI has no default endpoint (the API throws at call time), and
    // Ollama's default is localhost — inside HQ's API container that is the
    // container itself. The form marks the field required; honour it.
    if (llmProviderNeedsEndpoint(this.formData.type) && !this.formData.endpoint.trim()) {
      return false;
    }
    // On create an API key is required for every type EXCEPT Ollama (keyless).
    // On edit a blank key means "keep the existing key".
    if (!this.editingId && llmProviderNeedsKey(this.formData.type)) {
      return !!this.formData.apiKey.trim();
    }
    return true;
  }

  /**
   * Persist the create/edit form. The API key (when provided) is sent to the
   * API which encrypts it at rest; it is never echoed back. After saving, the
   * write-only `apiKey` field is cleared.
   */
  async saveProvider(): Promise<boolean> {
    if (!this.isFormValid) {
      const required = ["Type", "Name"];
      if (!this.editingId && llmProviderNeedsKey(this.formData.type)) required.push("an API Key");
      if (llmProviderNeedsEndpoint(this.formData.type)) required.push("an Endpoint");
      this.formError = `${required.slice(0, -1).join(", ")}${required.length > 2 ? "," : ""} and ${required[required.length - 1]} are required.`;
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      const payload: Record<string, unknown> = {
        type: this.formData.type,
        name: this.formData.name.trim(),
        enabled: !!this.formData.enabled,
      };
      // Only include the API key when the user actually entered one. On edit an
      // empty value keeps the existing encrypted key untouched. Ollama is
      // keyless, so a key is never sent for it.
      if (
        llmProviderNeedsKey(this.formData.type) &&
        this.formData.apiKey.trim()
      ) {
        payload.apiKey = this.formData.apiKey.trim();
      }
      // Send these explicitly (null when empty) so clearing a field — or
      // switching type — actually persists. apiVersion only applies to Azure
      // OpenAI; it's always cleared for other types so it can't be misapplied.
      payload.defaultModel = this.formData.defaultModel.trim() || null;
      payload.endpoint = this.formData.endpoint.trim() || null;
      payload.apiVersion =
        this.formData.type === "azureOpenAI"
          ? this.formData.apiVersion.trim() || null
          : null;

      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refusal as
      // null, which read as saved and closed the dialog with the reason lost.
      if (this.editingId) {
        await this.service?.patch(this.editingId, payload, THROW_ON_ERROR);
      } else {
        await this.service?.create(payload as never, THROW_ON_ERROR);
      }

      await this.reload();
      runInAction(() => {
        this.editingId = null;
        // Clear the write-only key so it never lingers in memory/state.
        this.formData = LlmProvidersStore.emptyForm();
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Failed to save provider";
        }
      });

      console.error("saveProvider failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /**
   * Delete a provider. Destructive and unrecoverable — the encrypted API key is
   * gone with the row — so the call site confirms first (ConfirmingIconButton).
   * A failure is surfaced through `error` (the store's existing error channel,
   * rendered as an alert by the body) rather than only the console: a silent
   * failure here reads as "the delete worked" until the next reload.
   */
  async removeProvider(id: string) {
    this.error = null;
    try {
      await this.service?.remove(id, THROW_ON_ERROR);
      // reload() clears `error` on entry, so a successful removal never leaves
      // a stale message behind.
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to remove provider";
        }
      });

      console.error("Failed to remove provider:", err);
    }
  }

  /**
   * Trigger the API's `validate` custom action for a saved provider. The API
   * decrypts the stored key in-memory and makes a probe call to the vendor,
   * reporting usability. No secret is sent from the client — only the action +
   * id.
   */
  async testProvider(id: string): Promise<void> {
    this.testingId = id;
    this.lastTestResult = null;
    try {
      const payload: LlmProviderValidatePayload = { action: "validate", id };
      // The validate action POSTs `{ action, id }` to the service; the response
      // is the validation result, not a provider entity.
      const response = (await this.service?.create(
        payload as never,
        THROW_ON_ERROR,
      )) as unknown as LlmProviderValidateResponse;
      runInAction(() => {
        this.lastTestResult = {
          status: response.validation?.status ?? response.status ?? "error",
          message: response.validation?.message ?? response.message,
        };
      });
      // Reload so the freshly-computed status/lastValidatedAt show in the list.
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.lastTestResult = {
            status: "error",
            message: err instanceof Error ? err.message : "Provider test failed",
          };
        }
      });

      console.error("testProvider failed:", err);
    } finally {
      runInAction(() => {
        this.testingId = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.loadFailed = false;
    this.searchQuery = "";
    this.typeFilter = "all";
    this.editingId = null;
    this.formData = LlmProvidersStore.emptyForm();
    this.saving = false;
    this.formError = null;
    this.testingId = null;
    this.lastTestResult = null;
  }
}

export default LlmProvidersStore;
